import React, { createContext, useCallback, useContext, useState } from 'react';

interface BackdropContextType {
  isBackdropActive: boolean;
  activeMenuId: string | null;
  showBackdrop: (menuId?: string) => void;
  hideBackdrop: (menuId?: string) => void;
}

const BackdropContext = createContext<BackdropContextType | undefined>(
  undefined,
);

interface BackdropProviderProps {
  children: React.ReactNode;
}

/**
 * Backdrop Provider for managing global menu blur effects
 * Tracks when any menu/dropdown is open and should blur the background
 */
export const BackdropProvider: React.FC<BackdropProviderProps> = ({
  children,
}) => {
  const [isBackdropActive, setIsBackdropActive] = useState(false);
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const [_openMenus, setOpenMenus] = useState<Set<string>>(new Set());

  const showBackdrop = useCallback((menuId: string = 'default') => {
    setOpenMenus((prev) => {
      const newSet = new Set(prev);
      newSet.add(menuId);
      return newSet;
    });
    setActiveMenuId(menuId);
    setIsBackdropActive(true);
  }, []);

  const hideBackdrop = useCallback((menuId: string = 'default') => {
    setOpenMenus((prev) => {
      const newSet = new Set(prev);
      newSet.delete(menuId);

      // If no menus are open, hide backdrop
      if (newSet.size === 0) {
        setIsBackdropActive(false);
        setActiveMenuId(null);
      } else {
        // Set active menu to the last remaining open menu
        const remainingMenus = Array.from(newSet);
        setActiveMenuId(remainingMenus[remainingMenus.length - 1]);
      }

      return newSet;
    });
  }, []);

  const value: BackdropContextType = {
    isBackdropActive,
    activeMenuId,
    showBackdrop,
    hideBackdrop,
  };

  return (
    <BackdropContext.Provider value={value}>
      {children}
    </BackdropContext.Provider>
  );
};

/**
 * Hook to use the backdrop context
 */
export const useBackdrop = (): BackdropContextType => {
  const context = useContext(BackdropContext);
  if (context === undefined) {
    throw new Error('useBackdrop must be used within a BackdropProvider');
  }
  return context;
};

/**
 * Hook for menu components to manage their backdrop state
 */
export const useMenuBackdrop = (menuId: string) => {
  const { showBackdrop, hideBackdrop } = useBackdrop();

  const showMenuBackdrop = useCallback(() => {
    showBackdrop(menuId);
  }, [showBackdrop, menuId]);

  const hideMenuBackdrop = useCallback(() => {
    hideBackdrop(menuId);
  }, [hideBackdrop, menuId]);

  return { showMenuBackdrop, hideMenuBackdrop };
};
